/**
 * Platform admin — SAM Session Log Viewer
 *
 * Two-panel layout:
 *  Left  — paginated, searchable list of agent sessions (summary row per session)
 *  Right — selected session: full entry timeline colour-coded by entry type
 *
 * Entry types rendered:
 *   outbound/trigger    🚀 context sent to OpenAI at session start
 *   outbound/other      📌 mid-session notification injected
 *   session_updated     🔄 agent page swap confirmed by OpenAI
 *   user                💬 user utterance (right-aligned)
 *   assistant           🤖 SAM response (left-aligned)
 *   tool_call           🔧 tool invoked
 *   tool_result         ✅/⚠  tool result
 */
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  useListAgentSessionsQuery,
  useGetAgentSessionQuery,
  type AgentSessionSummary,
} from '@/redux/internal-services/platformAdminApi'
import SessionDebugger from './SessionDebugger'
import { Button } from '@/components/UI/Button'
import { EmptyState } from '@/components/UI/EmptyState'
import { LoadingState } from '@/components/UI/LoadingState'
import { fieldClass } from '@/components/UI/fieldStyles'
import { cn } from '@/utils/cn'

// ── helpers ──────────────────────────────────────────────────────────────────

function fmtDate(iso: string) {
  try { return new Date(iso).toLocaleString() } catch { return iso }
}

function shortId(id?: string | null) {
  // tenant_id is null for platform / no-tenant sessions — guard so the list/detail don't crash.
  if (!id) return '—'
  return id.length > 12 ? id.slice(0, 8) + '…' + id.slice(-4) : id
}

// ── Session detail panel ──────────────────────────────────────────────────────

const SessionDetail: React.FC<{ sessionId: string }> = ({ sessionId }) => {
  const { t } = useTranslation()
  const { data, isLoading, isError } = useGetAgentSessionQuery(sessionId)
  const session = data?.data

  if (isLoading) return <LoadingState label="Loading session…" className="h-full" />
  if (isError || !session) return <div className="flex items-center justify-center h-full text-danger-text text-sm">{t('platform.sessionLoadFailed')}</div>

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="shrink-0 border-b border-ui-border p-4">
        <p className="text-sm font-semibold text-ui-text">{session.user_name || shortId(session.user_id)}</p>
        <p className="text-xs font-mono text-ui-text3 mt-0.5">{session.session_id}</p>
        <div className="flex items-center gap-3 mt-1 text-xs text-ui-text3">
          <span>agent: <strong className="text-ui-text2">{session.agent_type}</strong></span>
          <span>tenant: <strong className="text-ui-text2">{shortId(session.tenant_id)}</strong></span>
          <span>{session.entries.length} events</span>
        </div>
      </div>

      {/* Timeline — interactive, filterable, searchable debugger */}
      <div className="flex-1 min-h-0">
        <SessionDebugger entries={session.entries} />
      </div>
    </div>
  )
}

// ── Page ─────────────────────────────────────────────────────────────────────

const SessionLogsPage: React.FC = () => {
  const [search, setSearch] = useState('')
  const [agentType, setAgentType] = useState('')
  const [page, setPage] = useState(1)
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const { data, isLoading } = useListAgentSessionsQuery({
    search: search || undefined,
    agentType: agentType || undefined,
    page,
    limit: 50,
  })

  const sessions: AgentSessionSummary[] = data?.data ?? []
  const total = data?.total ?? 0

  return (
    <div className="flex h-full min-h-0">

      {/* ── Left: session list ── */}
      <div className="flex w-80 shrink-0 flex-col border-r border-ui-border bg-ui-card">
        {/* Filters */}
        <div className="space-y-2 border-b border-ui-border p-3">
          <h1 className="text-h2 text-ui-text">Session Logs</h1>
          <input
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1) }}
            placeholder="Search by user name, session ID…"
            className={fieldClass({ size: 'sm' })}
          />
          <select
            value={agentType}
            onChange={e => { setAgentType(e.target.value); setPage(1) }}
            className={fieldClass({ size: 'sm' })}
          >
            <option value="">All agent types</option>
            <option value="onboarding">onboarding</option>
          </select>
          <p className="text-xs text-ui-text3">{total} sessions</p>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto">
          {isLoading && <LoadingState label="Loading…" className="py-8" />}
          {!isLoading && sessions.length === 0 && (
            <EmptyState title="No sessions found." className="py-8" />
          )}
          {sessions.map(s => (
            <button
              key={s.session_id}
              onClick={() => setSelectedId(s.session_id)}
              className={cn(
                'w-full border-b border-ui-border px-3 py-2.5 text-left transition-colors hover:bg-ui-hover focus-ring',
                selectedId === s.session_id && 'border-l-2 border-l-accent bg-accent/10',
              )}
            >
              <p className="text-xs font-medium text-ui-text truncate">{s.user_name || shortId(s.user_id)}</p>
              <p className="text-xs font-mono text-ui-text3 truncate">{s.session_id.slice(0, 16)}…</p>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-xs text-ui-text3">{fmtDate(s.started_at)}</span>
                <span className="text-xs text-ui-text3 ml-auto">{s.turn_count} turns · {s.entry_count} events</span>
              </div>
              <p className="text-xs text-ui-text3 truncate">{s.agent_type} · {shortId(s.tenant_id)}</p>
            </button>
          ))}
        </div>

        {/* Pagination */}
        {total > 50 && (
          <div className="flex items-center justify-between border-t border-ui-border p-2 text-xs text-ui-text2">
            <Button variant="ghost" size="xs" disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Prev</Button>
            <span>Page {page}</span>
            <Button variant="ghost" size="xs" disabled={sessions.length < 50} onClick={() => setPage(p => p + 1)}>Next →</Button>
          </div>
        )}
      </div>

      {/* ── Right: detail ── */}
      <div className="min-w-0 flex-1 bg-ui">
        {selectedId
          ? <SessionDetail sessionId={selectedId} />
          : (
            <EmptyState title="Select a session to view its timeline." className="h-full" />
          )
        }
      </div>

    </div>
  )
}

export default SessionLogsPage
